import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  startTransition,
} from 'react';
import {flushSync} from 'react-dom';
import {t} from 'i18next';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as UploadIcon} from '../../../../assets/images/upload.svg';
import ArrCheckbox from '../../../common/arr-checkbox/ArrCheckbox';
import {useDispatch, useSelector} from 'react-redux';
import {CustomMenuItem} from '../../../common/arr-menu/ArrMenu';
import {RootState} from '../../../../redux/store';
import {useSubTallyExport} from '../../../../hooks/reporting/useReporting';
import {
  convertToIsoFormat,
  getSubtallyActionPAU,
  getSubtallyActionRMU,
  handleExportSubtallyUtil,
  prepareRows,
  roles,
} from '../../../../utils/reportUtils';
import {
  SourceTypeCode,
  SourceTypeContextCode,
  ToastType,
} from '../../../../shared/constants';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material';

import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
export type TallyResultProps = {
  isSubTallyRun?: boolean | ISubtallyResultResponseBody;
  isTallyFlag?: boolean;
  asOfText?: string;
  handleLoading?: (value: boolean) => void;
};

import './SubTallyResult.scss';
import {ISubtallyResultResponseBody} from '../../../../api/types/reportingApi.type';
import {
  setActiveActionContext,
  updateTally,
} from '../../../../redux/slice/tallyReportSlice';
import TallyActions from '../tally-actions/TallyActions';

type Column = {
  key: string;
  label: string;
};
type SelectedMap = Record<string, boolean>;
interface TallyRow {
  selected: SelectedMap;
}

type PairObj = {header: string; label: string; value: number};

const formatUtcAsOf = (dt: Date): string => {
  const yyyy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  const hh = String(dt.getUTCHours()).padStart(2, '0');
  const mi = String(dt.getUTCMinutes()).padStart(2, '0');
  const ss = String(dt.getUTCSeconds()).padStart(2, '0');
  return `${yyyy}/${mm}/${dd} ${hh}:${mi}:${ss} UTC`;
};
type cellClicked = {rowId: number; colId: number};

const formatColumns = (columnData: any, label: any) => {
  const formattedColums = [
    {key: label, label: label},
    ...columnData.map((col: any, id: any) => ({key: `c${id}`, label: col})),
    {
      key: 'Unique Documents Count',
      label: 'Unique Documents Count',
    },
  ];
  return formattedColums;
};

const buildTableData = (
  rowList: string[], // all row names
  columnList: string[], // all column names including last "Unique Count"
  tallyList: any[], // raw tally data
  subTallyBy: string // dynamic first column key
) => {
  return rowList.map((row) => {
    // Base row object (first column with row label)
    const rowObj: Record<string, any> = {
      [subTallyBy]: row,
    };

    // Get any record for total (total is same for entire row)
    const baseMatch = tallyList.find((item) => item.tallyFieldValue === row);

    columnList.forEach((col: any) => {
      // Case 1: Last column (unique count)

      if (col === 'Unique Documents Count') {
        // eslint-disable-next-line security/detect-object-injection
        rowObj[col] = baseMatch?.total ?? 0;
        return;
      }
      // Case 2: Check if a record exists for this row+column
      const match = tallyList.find(
        (item) => item.tallyFieldValue === row && item.fieldValue === col
      );

      // Case 3: Insert docCount or 0 if missing
      // eslint-disable-next-line security/detect-object-injection
      rowObj[col] = match?.docCount ?? 0;
    });

    return rowObj;
  });
};

const SubTallyResult: React.FC<TallyResultProps> = ({
  isSubTallyRun,
  asOfText,
  handleLoading,
}) => {
  const tallyEnabled = useMemo(() => {
    return Boolean(isSubTallyRun);
  }, [isSubTallyRun]);

  const [noSubTallyData, setNoSubTallyData] = useState<boolean>(false);
  const [reportdata, setReportdata] = useState<ISubtallyResultResponseBody>();

  useEffect(() => {
    if (typeof isSubTallyRun != 'boolean') {
      setReportdata(isSubTallyRun);
    }
  }, [isSubTallyRun]);

  const computedAsOf = useMemo(
    () => formatUtcAsOf(new Date()),
    [isSubTallyRun]
  );
  const resolvedAsOf = asOfText ?? computedAsOf;

  const [showLoaderOverlay, setShowLoaderOverlay] = useState(false);
  const [columns, setColumn] = useState<Column[]>([]);
  const [isActionBtnDisabled, setIsActionBtnDisabled] = useState(false);
  const [cellClicked, setCellClicked] = useState<cellClicked>();
  const [isIndeterminate, setIsIndeterminate] = useState<boolean>(false);
  const makeSelectedTemplate = (col: any) =>
    Object.fromEntries(col?.map((c: any) => [c.key, false]));

  const [rows, setRows] = useState<TallyRow[]>([]);

  const [checkboxSelected, setCheckboxSelected] = useState<boolean>(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const {mutateAsync: getSubTallyExportReport} = useSubTallyExport();
  const dispatch = useDispatch();
  const [selectedPairs, setSelectedPairs] = useState<PairObj[]>([]);

  const keyOf = (pair: PairObj) =>
    `${pair.label}::${pair.header}::${pair.value}`;

  const addUniquePair = (prev: PairObj[], pair: PairObj) => {
    const set = new Set(prev.map(keyOf));
    const k = keyOf(pair);
    return set.has(k) ? prev : [...prev, pair];
  };

  const removePair = (prev: PairObj[], pair: PairObj) =>
    prev.filter((p) => keyOf(p) !== keyOf(pair));

  const firstCol = 0;
  const lastCol = columns.length - 1;

  const isMiddleCol = (idx: number) => idx !== firstCol && idx !== lastCol;

  const makePair = (row: any, col: Column): PairObj => ({
    label: row[columns[firstCol].label] as string, // row’s first column text
    header: col.label,
    value: row[col.label] as number,
  });

  useEffect(() => {
    if (reportdata?.columnList != undefined) {
      if (reportdata?.columnList?.length > 0) {
        const r = formatColumns(reportdata?.columnList, reportdata?.tallyBy);
        setColumn(r);
        const newColumn = [...(reportdata?.columnList ?? [])];
        const tableData = buildTableData(
          reportdata?.rowList,
          [...newColumn, 'Unique Documents Count'],
          reportdata?.tallyModel?.subTally?.tallyByCriteriaList,
          reportdata?.tallyBy
        );
        setRows(
          prepareRows(tableData)?.map((data: any) => ({
            ...data,
            selected: {...makeSelectedTemplate(r)},
          }))
        );
      }
    }
    if (reportdata?.rowList != undefined) {
      if (reportdata?.rowList?.length > 0) {
        setNoSubTallyData(false);
      } else {
        setNoSubTallyData(true);
      }
    }
  }, [reportdata?.columnList, reportdata?.rowList]);

  const [menuItem, setMenuItem] = useState<CustomMenuItem[]>(
    getSubtallyActionPAU(t)
  );
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const selectedSource = useSelector(
    (state: RootState) => state.tallyreport.selectedSource
  );
  const tallyData = useSelector(
    (state: RootState) => state.tallyreport.tallyData
  );

  const subTallyData = useSelector(
    (state: RootState) => state.tallyreport.subtallyData
  );

  const subTallyResult = useSelector(
    (state: RootState) => state.tallyreport.subtallyResult
  );

  const subTallyResponse = useSelector(
    (state: RootState) => state.tallyreport.subtallyResponse
  );

  const useCssVar = (name: string, fallback: string) =>
    useMemo(
      () =>
        getComputedStyle(document.documentElement)
          .getPropertyValue(name)
          .trim() || fallback,
      [name, fallback]
    );

  const borderBold = useCssVar('--border-bold', '#3c4f6b');
  const surfaceDim = useCssVar('--surface-dim', '#263449');
  const surfaceStrong = useCssVar('--surface-strong', '#0c1119');
  const surfaceHover = useCssVar('--surface-hover', '#1f2b3d');
  const surfaceMedium = useCssVar('--surface-medium', '#182231');

  useEffect(() => {
    if (loginUserData?.roleName === roles.projectAdmin) {
      setMenuItem(getSubtallyActionPAU(t));
    } else if (loginUserData?.roleName === roles.reviewManager) {
      setMenuItem(getSubtallyActionRMU(t));
    }
  }, [loginUserData?.roleName]);

  useEffect(() => {
    const anyTrue = rows?.some((row: any) =>
      Object.values(row?.selected).some((v: any) => v)
    );
    setIsActionBtnDisabled(anyTrue);

    const areAllValuesTrue = rows?.every((row: any) => {
      const keys = Object.keys(row?.selected ?? {});
      // eslint-disable-next-line security/detect-object-injection
      const middleValues = keys.slice(1, -1).map((k) => row?.selected[k]);
      return middleValues.every((v) => v);
    });
    if (areAllValuesTrue) {
      setCheckboxSelected(true);
      setIsIndeterminate(false);
    } else if (anyTrue) {
      setCheckboxSelected(true);
      setIsIndeterminate(true);
    } else {
      setCheckboxSelected(false);
      setIsIndeterminate(false);
    }
  }, [rows]);

  const triggerToast = (message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const handleUploadClick = useCallback(async () => {
    const sourceData = subTallyResult?.docs.map(
      ({label, header, value}: any) => ({
        fileType: label,
        columnName: header,
        columnValue: `${value}`,
      })
    );
    const sourceType = `${subTallyData?.idLabels.map((item: any) => item.Label).join(',')}`;
    const subTallyBy = `${subTallyData?.label} : ${sourceType}`;
    const tallySelectType = `${tallyData?.label}`;
    const filterTallySelectionsView = Object.values(subTallyData?.filters || {})
      .map((item: any) => {
        if (!item?.label) return null;
        const selectionType = item.selectionType
          ? item.selectionType.charAt(0).toUpperCase() +
            item.selectionType.slice(1)
          : '';
        if (item?.type === 'INT') {
          return `${item.label}: ${selectionType} ${item?.startSize} ${selectionType === 'Between' ? `to ${item?.endSize}` : ''}`;
        }
        if (item?.type === 'DATETIME') {
          return `${item.label}: ${selectionType} ${item?.startDate} ${selectionType === 'Between' ? `to ${item?.endDate}` : ''}`;
        }
        const values =
          item.values?.map((v: any) => `"${v.text}"`).join(',') || '';

        return `${item.label} - ${selectionType}: ${values}`;
      })
      .filter(Boolean)
      .join(',');
    handleLoading?.(true);
    const {success, data} = await handleExportSubtallyUtil({
      subTallyResponse: subTallyResponse ?? '',
      selectedSource,
      sourceType,
      filterTallySelectionsView,
      subTallyBy,
      tallySelectType,
      sourceData,
      dateTime: resolvedAsOf.trimEnd().replace(/UTC$/, ''),
      getSubTallyExportReport,
    });

    if (success && data) {
      handleLoading?.(false);
      triggerToast(t('reports.common.reportExportSuccess'), [
        ToastType.Success,
      ]);
    } else {
      handleLoading?.(false);
      triggerToast(t('reports.common.reportExportError'), [ToastType.Error]);
    }
  }, [
    getSubTallyExportReport,
    subTallyResponse,
    subTallyData,
    tallyData,
    selectedSource,
    subTallyResult,
  ]);

  const handleSelectAllMiddleCells = () => {
    flushSync(() => {
      setShowLoaderOverlay(true);
    });
    startTransition(() => {
      setRows((prev: any) => {
        const newRows = prev.map((row: any) => {
          const newSelected = {...row.selected};
          columns.forEach((c, idx) => {
            if (isMiddleCol(idx)) {
              !checkboxSelected
                ? (newSelected[c.key] = true)
                : (newSelected[c.key] = false);
            } else {
              newSelected[c.key] = false;
            }
          });
          return {...row, selected: newSelected};
        });

        if (!checkboxSelected) {
          const middleCols = columns.filter((_, i) => isMiddleCol(i));
          const allPairs = newRows.flatMap((row: any) =>
            middleCols
              .filter((c) => row.selected[c.key])
              .map((c) => makePair(row, c))
          );
          setSelectedPairs(allPairs);
        } else {
          setSelectedPairs([]);
        }

        return newRows;
      });

      setCheckboxSelected((prev) => !prev);
      setShowLoaderOverlay(false);
    });
  };

  const handleCellClick = (value: any, rowIndex: number, colIndex: number) => {
    const key = columns.at(colIndex)?.key;
    if (!key) return;

    if (colIndex === lastCol) return;
    flushSync(() => setShowLoaderOverlay(true));

    startTransition(() => {
      setIsIndeterminate(false);
      setCheckboxSelected(false);
      setCellClicked({rowId: rowIndex, colId: colIndex});

      const key = columns.at(colIndex)?.key;
      if (!key) return;

      if (colIndex === lastCol) return;

      setRows((prev: any) =>
        prev?.map((row: any, rIdx: number) => {
          if (rIdx !== rowIndex) return row;

          const newSelected: Record<string, boolean> = {...row?.selected};

          if (colIndex === firstCol) {
            const allMiddleSelected = columns
              .filter((_, idx) => isMiddleCol(idx))
              .every((c) => row.selected[c.key]);

            columns.forEach((c, i) => {
              if (isMiddleCol(i)) {
                newSelected[c.key] = !allMiddleSelected;
              } else {
                newSelected[c.key] = false;
              }
            });

            setSelectedPairs((prevPairs) => {
              const middleCols = columns.filter((_, i) => isMiddleCol(i));
              const rowMiddlePairs = middleCols.map((c) => makePair(row, c));

              let next = prevPairs;
              for (const rp of rowMiddlePairs) {
                next = removePair(next, rp);
              }

              const toAdd = middleCols
                .filter((c) => newSelected[c.key])
                .map((c) => makePair(row, c));

              for (const ap of toAdd) {
                next = addUniquePair(next, ap);
              }

              return next;
            });

            return {...row, selected: newSelected};
          }

          newSelected[key] = !newSelected[key];

          const col = columns[colIndex];
          const pair = makePair(row, col);
          setSelectedPairs((prevPairs) => {
            if (newSelected[key]) {
              return addUniquePair(prevPairs, pair);
            }
            return removePair(prevPairs, pair);
          });

          return {...row, selected: newSelected};
        })
      );

      setShowLoaderOverlay(false);
    });
  };

  useEffect(() => {
    dispatch(
      updateTally({
        subtallyResult: {
          dataasof: convertToIsoFormat(computedAsOf),
          list: transformRows(rows),
          docs: selectedPairs,
        },
      })
    );
  }, [selectedPairs]);

  type OutputItem = {
    tallyFieldValue: string | null;
    fieldValue: string;
    docCount: number;
    docVolume: string;
    isDocCountView: boolean;
    total: number;
  };

  const transformRows = <T extends object>(
    rows: T[],
    totalKey: string = 'Unique Documents Count'
  ): OutputItem[] => {
    const STATIC = new Set<string>([totalKey, 'id', 'selected']);
    const out: OutputItem[] = [];

    for (const row of rows) {
      const r = row as Record<string, unknown>;

      const total = Number(r[totalKey]) || 0;

      let tally: string | null = null;
      for (const [k, v] of Object.entries(r)) {
        if (!STATIC.has(k) && typeof v === 'string') {
          tally = v;
          break;
        }
      }

      for (const [k, v] of Object.entries(r)) {
        if (STATIC.has(k) || typeof v === 'string') continue;
        const num = Number(v);
        if (!Number.isFinite(num) || num <= 0) continue;

        out.push({
          tallyFieldValue: tally,
          fieldValue: k,
          docCount: num,
          docVolume: '',
          isDocCountView: true,
          total,
        });
      }
    }

    return out;
  };

  return (
    <Box data-testid="subTally-result-panel" className="subTally-result-panel">
      <ArrLoadingSpinner
        show={showLoaderOverlay}
        overlay={true}
        isOpen={true}
      />
      <div
        className="subtally-result-toolbar"
        data-testid={`sub-tally-result-data-as-of-select-all`}
      >
        <div
          className="tally-result-time-section"
          data-testid={`sub-tally-result-data-as-of`}
        >
          <Typography
            className="typo-text body4_size"
            data-testid={`sub-tally-result-data-as-of-text`}
          >
            {t('reports.tally.tallyResult.dataAsOf', {
              defaultValue: 'Data as of:',
            })}
            {tallyEnabled && (
              <span className="asof-date" data-testid={`sub-tally-result-span`}>
                {resolvedAsOf}
              </span>
            )}
          </Typography>
        </div>

        <div
          className="header-button-div"
          aria-disabled={!tallyEnabled || noSubTallyData}
          data-testid={`sub-tally-result-select-all`}
        >
          <div
            className="select-all-div"
            aria-disabled={!tallyEnabled || noSubTallyData}
            data-testid={`sub-tally-result-select-all-btn`}
          >
            <ArrCheckbox
              checked={tallyEnabled && !noSubTallyData && checkboxSelected}
              isIndeterminate={isIndeterminate}
              onChange={handleSelectAllMiddleCells}
              size="medium"
              disabled={!tallyEnabled || noSubTallyData}
              aria-label={t('reports.tally.tallyResult.selectAll', {
                defaultValue: 'Select all',
              })}
            />
            <Typography
              className="typo-text body3_size"
              data-testid={`sub-tally-result-select-all-text`}
            >
              {t('reports.tally.tallyResult.selectAll', {
                defaultValue: 'Select all',
              })}
            </Typography>
          </div>

          <Box
            className="table-header-section"
            aria-disabled={!tallyEnabled || !isActionBtnDisabled}
          >
            <ArrIconButton
              icon={
                <UploadIcon
                  className={`${!tallyEnabled || !isActionBtnDisabled ? 'inactive-upload-icon' : 'upload-icon'}`}
                />
              }
              onClick={handleUploadClick}
              ariaLabel={t('reports.tally.tallyResult.upload', {
                defaultValue: 'upload',
              })}
              shape={!tallyEnabled || !isActionBtnDisabled ? 'none' : 'default'}
              size="medium"
              className="upload-icon-btn"
              testId="upload-button"
              disabled={!tallyEnabled || !isActionBtnDisabled}
            />
            <TallyActions
              actions={menuItem}
              isDisabled={!tallyEnabled || !isActionBtnDisabled}
              sourceType={SourceTypeCode.SUBTALLY}
              dataTestId="subtally-action-menu"
              direction="bottom-left"
              subMenuDirection="left"
              sourceTypeContext={SourceTypeContextCode.SUBTALLY}
              size="small"
              onActionClick={() =>
                dispatch(setActiveActionContext(SourceTypeCode.SUBTALLY))
              }
            />
          </Box>
        </div>
      </div>
      {tallyEnabled && !noSubTallyData ? (
        <div className="table-main" data-testid={`sub-tally-result-div`}>
          <Table className="table-container" data-testid="table">
            <TableHead className="table-head">
              <TableRow className="table-headRow">
                {columns?.map((header: any, idx: number) => (
                  <TableCell
                    key={header.label}
                    className={
                      lastCol == idx
                        ? `table-header1`
                        : idx == 0
                          ? `table-header2`
                          : `table-header`
                    }
                    sx={{
                      height: 28,
                      lineHeight: '28px',
                      width: 160,
                      whiteSpace: 'nowrap',
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      padding: 0,
                      position:
                        idx == 0
                          ? 'sticky'
                          : idx == lastCol - 1
                            ? ' '
                            : 'relative',

                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: '15%',
                        bottom: '15%',
                        width: '1px',
                        backgroundColor: borderBold,
                      },
                    }}
                    data-testid={`tagging-widget-table-column-${header.key}`}
                  >
                    <Tooltip
                      title={header?.label}
                      // disableHoverListener={header?.label?.length <= 14}
                    >
                      <span
                        data-testid={`sub-tally-result-column-header-${header.key}`}
                      >
                        {header.label}
                      </span>
                    </Tooltip>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map((row: any, rIdx: number) => (
                <TableRow key={row.id} sx={{height: '36px'}}>
                  {columns?.map((col: any, cIdx) => (
                    <TableCell
                      key={col.key}
                      className={
                        lastCol == cIdx ? 'table-row-final' : 'table-row'
                      }
                      onClick={() =>
                        handleCellClick(row[col.label], rIdx, cIdx)
                      }
                      sx={{
                        width: 160,
                        whiteSpace: 'nowrap',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        position: cIdx == 0 ? 'sticky !important' : '',
                        left: 0,
                        backgroundColor:
                          cIdx === lastCol
                            ? surfaceDim // last column
                            : row.selected[col.key]
                              ? surfaceStrong // selected
                              : surfaceMedium, // deselected
                        cursor: cIdx === lastCol ? 'default' : 'pointer',
                        '&:hover':
                          (cellClicked?.rowId == rIdx &&
                            cellClicked?.colId == cIdx) ||
                          cIdx === lastCol
                            ? {} // disable hover if selected or last column
                            : {backgroundColor: surfaceHover},
                      }}
                      data-testid={`sub-tally-result-table-cell-${rIdx}-${cIdx}`}
                    >
                      <Tooltip
                        title={row[col.label]}
                        // disableHoverListener={row[col.label]?.length <= 18}
                      >
                        <span
                          data-testid={`sub-tally-result-title-${rIdx}-${cIdx}`}
                        >
                          {row[col.label]}
                        </span>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div
          className="empty-container"
          data-testid={`sub-tally-result-container-empty`}
        >
          <Box
            className="tally-empty-state"
            role="status"
            aria-live="polite"
            data-testid="tally-empty-state"
          >
            <Typography
              className="empty-text typo-text body3_size"
              data-testid={`sub-tally-result-no-data-subtally`}
            >
              {noSubTallyData
                ? t('reports.tally.tallyResult.noData', {
                    defaultValue: 'Data not available',
                  })
                : t('reports.tally.tallyResult.runTallyMessage', {
                    defaultValue: 'Run Subtally to view the results',
                  })}
            </Typography>
          </Box>
        </div>
      )}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
    </Box>
  );
};

export default SubTallyResult;
